import React, { useState, useRef, useEffect, useCallback } from "react";
import countryCodes from "../../../services/countryCodes.json";

/**
 * CountryCodePicker
 * A custom flag-aware country code dropdown that replaces the native <select>.
 *
 * Props:
 *   name        {string}   - The form field name / controlId (used on the hidden input)
 *   id          {string}   - Element id
 *   value       {string}   - Current value (format: "+1_US" i.e. dial_code + "_" + code)
 *   onChange    {fn}       - Called with a synthetic-like event { target: { name, value, type } }
 *   disabled    {boolean}  - Disables interaction
 */
export default function CountryCodePicker({
  name,
  id,
  value,
  onChange,
  disabled,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Derive the selected country object from value string e.g. "+1_US"
  const selectedCountry = value
    ? countryCodes.find(
        (c) => c.dial_code + "_" + c.code === value
      ) || null
    : null;

  const selectedCode = selectedCountry?.code?.toLowerCase() || "";

  // Filtered list based on search input
  const filteredCodes = search
    ? countryCodes.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dial_code.includes(search) ||
          c.code.toLowerCase().includes(search.toLowerCase())
      )
    : countryCodes;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Focus search box when dropdown opens
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
    setSearch("");
  };

  const handleSelect = useCallback(
    (item) => {
      const val = item.dial_code + "_" + item.code;
      onChange({ target: { name, value: val, type: "dropdown" } });
      setOpen(false);
      setSearch("");
    },
    [name, onChange]
  );

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ target: { name, value: "", type: "dropdown" } });
    setOpen(false);
    setSearch("");
  };

  return (
    <div
      className="wf-cc-picker"
      ref={containerRef}
      style={{ position: "relative", height: "100%" }}
    >
      {/* Trigger Button */}
      <button
        type="button"
        className={`wf-cc-trigger${open ? " wf-cc-trigger--open" : ""}${disabled ? " wf-cc-trigger--disabled" : ""}`}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        tabIndex={0}
      >
        {selectedCountry ? (
          <>
            <img
              src={`https://flagcdn.com/w40/${selectedCode}.png`}
              alt={selectedCountry.code}
              className="wf-cc-flag"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <span className="wf-cc-dial">{selectedCountry.dial_code}</span>
          </>
        ) : (
          <>
            <i className="fa fa-globe wf-cc-globe" aria-hidden="true" />
            <span className="wf-cc-placeholder">Code</span>
          </>
        )}
        <i className={`fa fa-chevron-down wf-cc-caret${open ? " wf-cc-caret--up" : ""}`} aria-hidden="true" />
      </button>

      {/* Dropdown Panel */}
      {open && (
        <div className="wf-cc-dropdown" role="listbox">
          {/* Search */}
          <div className="wf-cc-search-wrap">
            <i className="fa fa-search wf-cc-search-icon" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              className="wf-cc-search"
              placeholder="Search country..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {search && (
              <button
                type="button"
                className="wf-cc-search-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearch("");
                }}
                tabIndex={-1}
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>

          {/* Clear selection option */}
          {selectedCountry && (
            <div
              className="wf-cc-option wf-cc-option--clear"
              role="option"
              onClick={handleClear}
            >
              <i className="fa fa-times-circle" style={{ color: "#94a3b8", marginRight: 6 }} />
              <span>Clear selection</span>
            </div>
          )}

          {/* Country list */}
          <ul className="wf-cc-list" ref={listRef}>
            {filteredCodes.length === 0 ? (
              <li className="wf-cc-no-results">No results found</li>
            ) : (
              filteredCodes.map((item) => {
                const isSelected =
                  selectedCountry?.code === item.code &&
                  selectedCountry?.dial_code === item.dial_code;
                return (
                  <li
                    key={item.code + item.dial_code}
                    className={`wf-cc-option${isSelected ? " wf-cc-option--selected" : ""}`}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(item)}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${item.code.toLowerCase()}.png`}
                      alt={item.code}
                      className="wf-cc-flag"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <span className="wf-cc-country-name">{item.name}</span>
                    <span className="wf-cc-dial-badge">{item.dial_code}</span>
                    {isSelected && (
                      <i className="fa fa-check wf-cc-check" aria-hidden="true" />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
